import React, { useState } from 'react';
import { Card } from '../ui/card';
import { gameAPI } from '../../services/gameAPI';
import { featuredSessionId } from '../../utils/featuredSession';
import { toast } from '../../hooks/use-toast';
import { getThemeStyle } from '../home/themeStyles';
import matchesFocusVisible from '../home/matchesFocusVisible';

const STATUS_LABELS = {
  not_started: 'Not started',
  in_progress: 'In progress',
  won: 'Won',
  lost: 'Lost',
};

const FeaturedCaseCard = ({ caseInfo, onOpen }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const theme = getThemeStyle(caseInfo.theme);
  const hasStarted = caseInfo.status !== 'not_started';
  const isComplete = caseInfo.status === 'won' || caseInfo.status === 'lost';
  const actionLabel = isComplete ? 'Review' : hasStarted ? 'Resume' : 'Start';
  const statusLabel = STATUS_LABELS[caseInfo.status] || caseInfo.status;
  const isActive = isHovered || isFocused;
  const openCase = () => onOpen(caseInfo.id);

  // Play Again has to commit the restart BEFORE navigating: `FeaturedCaseEntry`
  // loads the session on mount, so navigating first would race the restart and
  // could hydrate the finished playthrough.
  //
  // `gameAPI.restartCase` is the compat route — the only restart there is.
  const handlePlayAgain = async (event) => {
    event.stopPropagation();
    if (restarting) return;
    setRestarting(true);
    try {
      await gameAPI.restartCase(featuredSessionId(caseInfo.id));
      openCase();
      // Deliberately left disabled: the navigation unmounts this card, and a
      // second restart while it is in flight would wipe the fresh board.
    } catch (error) {
      console.error('Failed to restart featured case:', error);
      toast({
        title: 'Restart failed',
        description: 'Could not restart this case. Please try again.',
        variant: 'destructive',
      });
      setRestarting(false);
    }
  };

  return (
    <Card
      data-testid="featured-case-card"
      data-case-id={caseInfo.id}
      className={`relative flex h-full flex-col overflow-hidden border-2 bg-slate-900 cursor-pointer transition-all duration-300 ${theme.border} ${theme.hoverBorder} ${
        isActive ? 'scale-105 shadow-2xl' : 'scale-100 shadow-lg'
      }`}
      style={{
        boxShadow: isActive ? `0 20px 60px rgba(${theme.accentRgb}, 0.4)` : undefined,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {caseInfo.card_image && !imageFailed ? (
        <div className="h-[180px] shrink-0 overflow-hidden">
          <img
            src={caseInfo.card_image}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setImageFailed(true)}
            className={`w-full h-full object-cover transition-transform duration-300 ${
              isActive ? 'scale-110' : 'scale-100'
            }`}
          />
        </div>
      ) : (
        <div
          data-testid="featured-case-image-placeholder"
          aria-hidden="true"
          className={`h-[180px] shrink-0 bg-gradient-to-br ${theme.gradient}`}
        />
      )}

      {/* `flex-1` is the fix for the dark strip: the grid stretches the Card, not this wrapper,
          so a content-height gradient left the Card's own `bg-slate-900` showing below it. */}
      <div
        data-testid="featured-case-body"
        className={`p-6 min-h-[270px] flex-1 flex flex-col bg-gradient-to-br ${theme.gradient}`}
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${theme.accentText}`}>
              {theme.label}
            </p>
            {/* The badge is the label, not the rule: featured cases run on the
                easy mechanics and ship `display_difficulty` for what the
                player reads (backend `catalog.FEATURED_DISPLAY_DIFFICULTY`).
                The fallback keeps a card without the field rendering. */}
            <p className="text-xs uppercase tracking-wider text-gray-300 mt-1">
              {caseInfo.display_difficulty || caseInfo.difficulty}
            </p>
          </div>
          <span
            data-testid="featured-case-status"
            className="rounded-full border border-white/20 bg-black/30 px-3 py-1 text-xs font-semibold text-white"
          >
            {statusLabel}
          </span>
        </div>

        <h3 className="text-2xl font-bold text-white mb-3">{caseInfo.title}</h3>
        <p className="text-gray-200 leading-relaxed flex-1 pb-6">{caseInfo.tagline}</p>

        {/* The action row is now two buttons wide. `mt-auto` stays on the
            primary button as well: it is the defense-in-depth that keeps the
            row pinned if the tagline ever loses `flex-1`. */}
        <div className="mt-auto flex items-end gap-4">
        <button
          type="button"
          data-testid="featured-case-action"
          aria-label={`${actionLabel} ${caseInfo.title}, ${statusLabel}`}
          className={`mt-auto flex flex-1 items-center justify-between border-0 bg-transparent p-0 text-left ${theme.accentText} ${theme.focusRing} font-semibold uppercase tracking-wider text-sm after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset`}
          onClick={openCase}
          onFocus={(event) => setIsFocused(matchesFocusVisible(event.currentTarget))}
          onBlur={() => setIsFocused(false)}
        >
          <span>{actionLabel}</span>
          <svg
            aria-hidden="true"
            className={`w-6 h-6 transition-transform duration-300 ${
              isActive ? 'translate-x-2' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>

        {/* Secondary to Review. `relative` lifts it clear of the primary
            button's full-card `::after` hit area. */}
        {isComplete && (
          <button
            type="button"
            data-testid="featured-case-play-again"
            aria-label={`Play ${caseInfo.title} again`}
            disabled={restarting}
            className={`relative shrink-0 rounded-full border border-white/20 bg-black/30 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gray-200 transition-colors hover:border-white/40 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/60 disabled:cursor-not-allowed disabled:opacity-60`}
            onClick={handlePlayAgain}
            onFocus={(event) => setIsFocused(matchesFocusVisible(event.currentTarget))}
            onBlur={() => setIsFocused(false)}
          >
            <span aria-live="polite">{restarting ? 'Restarting...' : 'Play Again'}</span>
          </button>
        )}
        </div>
      </div>
    </Card>
  );
};

export default FeaturedCaseCard;
